import { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { SourceCard } from './SourceCard';
import { toast } from 'sonner';

interface Doc {
  id: string;
  file_name: string;
  source_type: string;
  processing_status: string;
  file_path: string;
}

interface SourcesPanelProps {
  sessionId: string | null;
}

export function SourcesPanel({ sessionId }: SourcesPanelProps) {
  const { profile } = useAuth();
  const [tab, setTab] = useState<'sources' | 'notes'>('sources');
  const [docs, setDocs] = useState<Doc[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryCountRef = useRef(0);

  // Load existing docs for session
  useEffect(() => {
    if (!sessionId) return;
    supabase
      .from('regulatory_documents')
      .select('id, file_name, source_type, processing_status, file_path')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (data) setDocs(data);
      });
  }, [sessionId]);

  // Poll processing docs
  const MAX_POLL_RETRIES = 40; // 40 × 3 s = 2 min max before marking stuck docs failed

  useEffect(() => {
    const processingIds = docs
      .filter((d) => d.processing_status === 'processing')
      .map((d) => d.id);

    if (processingIds.length === 0) {
      if (pollRef.current) clearInterval(pollRef.current);
      retryCountRef.current = 0;
      return;
    }

    pollRef.current = setInterval(async () => {
      retryCountRef.current += 1;

      if (retryCountRef.current > MAX_POLL_RETRIES) {
        clearInterval(pollRef.current);
        setDocs((prev) =>
          prev.map((d) =>
            d.processing_status === 'processing'
              ? { ...d, processing_status: 'failed' }
              : d
          )
        );
        return;
      }

      try {
        const { data } = await supabase
          .from('regulatory_documents')
          .select('id, processing_status')
          .in('id', processingIds);
        if (data) {
          setDocs((prev) =>
            prev.map((d) => {
              const updated = data.find((u) => u.id === d.id);
              return updated ? { ...d, processing_status: updated.processing_status } : d;
            })
          );
        }
      } catch (err) {
        console.error('SourcesPanel poll error:', err);
        // Single error does not clear — retry count handles termination
      }
    }, 3000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [docs]);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!sessionId || !profile) return;
      setUploading(true);

      const filePath = `${profile.clinic_id}/${sessionId}/${file.name}`;

      try {
        const { error: upErr } = await supabase.storage
          .from('regulatory-library')
          .upload(filePath, file, { upsert: true });
        if (upErr) throw upErr;

        const { data: doc, error: insErr } = await supabase
          .from('regulatory_documents')
          .insert({
            clinic_id: profile.clinic_id,
            session_id: sessionId,
            file_name: file.name,
            file_path: filePath,
            source_type: 'federal',
            processing_status: 'processing',
          })
          .select('id, file_name, source_type, processing_status, file_path')
          .single();
        if (insErr) throw insErr;

        setDocs((prev) => [...prev, doc]);

        // Invoke processing edge function
        supabase.functions.invoke('process-regulatory-document', {
          body: { regulatory_doc_id: doc.id },
        });
      } catch (err: any) {
        toast.error(err.message || 'Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [sessionId, profile]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDeleted = (id: string) => {
    setDocs((prev) => prev.filter((d) => d.id !== id));
  };

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Tab toggle */}
      <div className="px-4 pt-4 pb-2 flex-shrink-0">
        <div className="flex gap-1 bg-secondary rounded-md p-1">
          <button
            onClick={() => setTab('sources')}
            className={`flex-1 text-xs font-medium py-1.5 rounded-sm transition-colors ${
              tab === 'sources' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Sources
          </button>
          <button
            onClick={() => setTab('notes')}
            className={`flex-1 text-xs font-medium py-1.5 rounded-sm transition-colors ${
              tab === 'notes' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Notes
          </button>
        </div>
      </div>

      {tab === 'notes' ? (
        <div className="flex-1 flex items-center justify-center px-4">
          <p className="text-sm text-muted-foreground text-center">Notes coming in next build.</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Upload button */}
          <div className="px-4 py-2 flex-shrink-0">
            <Button
              variant="outline"
              className="w-full justify-center gap-2"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !sessionId}
            >
              <Upload className="h-4 w-4" />
              {uploading ? 'Uploading...' : '+ Upload Document'}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.txt"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Source cards or empty state */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            {docs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                <FileText className="h-10 w-10 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">Upload your regulatory documents.</p>
                <p className="text-xs text-muted-foreground">Vera will only reference what's here.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {docs.map((doc) => (
                  <SourceCard
                    key={doc.id}
                    id={doc.id}
                    fileName={doc.file_name}
                    sourceType={doc.source_type}
                    processingStatus={doc.processing_status}
                    filePath={doc.file_path}
                    onDeleted={handleDeleted}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
